// Packages
import { defineStore } from 'pinia';

// Logic
import { hub_api } from '@global-client/logic/core/api';
import { decodeJWT, handleErrors, responseEqualToRequested } from '@global-client/logic/utils/mssUtils';
import { type YiviMountPoint, type YiviSession, startYiviAuthentication } from '@global-client/logic/utils/yiviHandler';

import filters from '@hub-client/logic/core/filters';
import { createLogger } from '@hub-client/logic/logging/Logger';
import { assert } from '@hub-client/logic/utils/assert';
import { delay } from '@hub-client/logic/utils/common';

// Models
import AuthenticationServer from '@global-client/models/MSS/Auths';
import PHCServer from '@global-client/models/MSS/PHC';
import { type AttrKeyResp, type AuthAttrKeyReq, type AuthStartReq, type CardReq, type LoginMethod, Source } from '@global-client/models/MSS/TAuths';
import {
	type DecodedAttributes,
	type EnterStartResp,
	type HubInfoResp,
	type InfoResp,
	ResultResponse,
	type ReturnCard,
	type SignedIdentifyingAttrs,
	isResult,
} from '@global-client/models/MSS/TGeneral';
import { type Attr, type Constellation, type HubInformation, PHCEnterMode, type UserSecretObject, isUserSecretObjectNew } from '@global-client/models/MSS/TPHC';
import Transcryptor from '@global-client/models/MSS/Transcryptor';
import { type ResolvedUserSecretObjects } from '@global-client/models/MSS/UserSecret';

// Stores
import { useGlobal } from '@global-client/stores/global';
import { useLocalStores } from '@global-client/stores/localStores';

import { FeatureFlag, useSettings } from '@hub-client/stores/settings';

const logger = createLogger('MSS');

// The Yivi session `enterPubHubs` is waiting on, plus a counter identifying the run that started it.
// Only one enter attempt can be in flight: `AuthenticationServer` holds a single authentication
// state, so a run that has been replaced must stop before it writes that state or completes an
// authentication against the state its replacement put there. Kept at module level because neither
// value is rendered.
let activeYiviSession: YiviSession | null = null;
let currentEnterRun = 0;
// The run that last claimed `currentEnterRun` for itself. `cancelEnter()` bumps the counter without
// putting a successor in place. The page uses it to make an issuance report `card_not_added`, so
// the counter alone cannot tell a run that was replaced from one that was cancelled with nothing
// taking over, and only the former can leave its cleanup to the run that took over.
let ownedEnterRun = 0;

/** Thrown by `enterPubHubs` when a newer run, or `cancelEnter()`, superseded it. */
class EnterCancelled extends Error {
	constructor() {
		super('The enter attempt was superseded');
		this.name = 'EnterCancelled';
	}
}

const useMSS = defineStore('mss', {
	state: () => {
		return {
			// * NOTE: Do not access this._authServer directly. Use getAuthServer() instead to make sure the server is initialized with the correct API url.
			_authServer: null as AuthenticationServer | null,
			// * NOTE: Do not access this._transcryptor directly. Use getTranscryptor() instead to make sure the server is initialized with the correct API url.
			_transcryptor: null as Transcryptor | null,
			phcServer: new PHCServer(),
			constellation: null as Constellation | null,
			hubs: null as Record<string, HubInformation> | null,
			// True while the disclosure has succeeded and the PubHubs card is being issued, so the
			// UI can tell the user the login worked and a second Yivi session is on its way.
			issuingCard: false,
			// True while the disclosure widget is the thing on screen, so a page that has to re-render
			// it (a breakpoint swap, a page restored from the back/forward cache) can tell whether a
			// restart still means anything.
			awaitingDisclosure: false,
		};
	},

	actions: {
		/**
		 * Stop the enter attempt in flight: abort the Yivi session it is showing and mark it
		 * superseded, so it gives up before touching the shared authentication state.
		 */
		cancelEnter(): void {
			currentEnterRun++;
			activeYiviSession?.abort();
			activeYiviSession = null;
			this.awaitingDisclosure = false;
			this.issuingCard = false;
		},

		/**
		 * Run a Yivi session and keep it cancellable for as long as it lasts.
		 *
		 * @param mountPoint The element the page wants the Yivi widget rendered into.
		 * @param ownedByRun The enter run this session belongs to; defaults to the run in flight, which
		 * is what a caller outside `enterPubHubs` - a card issuance retried from the page - wants.
		 * @returns The disclosure or issuance result.
		 */
		async runYiviSession(yiviRequestorUrl: string, request: string, mountPoint: YiviMountPoint, ownedByRun = currentEnterRun): Promise<string> {
			// A run that has been superseded renders no widget: it would draw over the one its
			// replacement is showing, and take the slot `cancelEnter()` reaches for with it.
			if (ownedByRun !== currentEnterRun) throw new EnterCancelled();

			const { session, result } = startYiviAuthentication(yiviRequestorUrl, request, mountPoint);
			activeYiviSession = session;
			try {
				return await result;
			} finally {
				// A newer run may already have put its own session here.
				if (activeYiviSession === session) activeYiviSession = null;
			}
		},

		/**
		 * Request the user secret objects, repairing a difference between the two stored copies where
		 * possible. See UserSecret.ts for how the two copies relate.
		 *
		 * @param signedIdentifyingAttrs A list of the signed identifying attributes that were disclosed in the enter request.
		 * @param afterReset Whether this call is the check performed after resetting usersecret to usersecretbackup.
		 * @returns The user secret objects, or the translation key of the error to show the user.
		 */
		async requestUserSecretObject(signedIdentifyingAttrs: SignedIdentifyingAttrs, afterReset: boolean = false): Promise<ResolvedUserSecretObjects> {
			return await this.phcServer.resolveUserSecretObject(
				signedIdentifyingAttrs,
				(userSecretObject) => this.requestAttrKeys(userSecretObject, signedIdentifyingAttrs),
				afterReset,
			);
		},

		async requestAttrKeys(
			userSecretObject: UserSecretObject | null,
			signedIdentifyingAttrs: SignedIdentifyingAttrs,
		): Promise<{ error: false; response: Record<string, AttrKeyResp> } | { error: true; response: string }> {
			const authServer = await this.getAuthServer();
			// Request attribute keys for all identifying attributes used to login.
			const attrKeyReq = this.buildAttributeKeyRequest(signedIdentifyingAttrs, userSecretObject);
			return await authServer.attrKeysEP(attrKeyReq);
		},

		async enterPubHubs(
			loginMethod: LoginMethod,
			enterMode: PHCEnterMode,
			mountPoint: YiviMountPoint,
			registerOnlyWithUniqueAttrs = false,
		): Promise<{ key: string; values?: string[] } | undefined> {
			// Take over from whatever was in flight - a page restarting its widget, or a second login
			// click - so only one run can reach the shared authentication state.
			this.cancelEnter();
			const runId = currentEnterRun;
			ownedEnterRun = runId;
			const superseded = () => runId !== currentEnterRun;
			// Whether a newer run has taken over the entry, and with it the cleanup this run would
			// otherwise have to do itself. See `ownedEnterRun`.
			const replaced = () => ownedEnterRun !== runId;

			const settings = useSettings();
			const cardFeature = settings.isFeatureEnabled(FeatureFlag.phCard);
			const authServer = await this.getAuthServer();
			let warningMessage = undefined;

			// 1. Fetch supported attr types
			const supported = await authServer.welcomeEPAuths();
			const identifyingAttrs = authServer.checkAttributes(supported, loginMethod, enterMode);

			// 2. Build auth start request
			const isRegistering = enterMode === PHCEnterMode.LoginOrRegister || enterMode === PHCEnterMode.Register;

			// Disable chained-sessions while the error fallback is not working
			// and there are timeouts with slow connectivity
			const chainedSession = false;
			// Below the original code before we disabled chained-session temporarily
			// const chainedSession = isRegistering && cardFeature;

			const authStartReq: AuthStartReq = {
				source: loginMethod.source,
				attr_types: !isRegistering && cardFeature ? loginMethod.login_attr : loginMethod.register_attr,
				attr_type_choices: [],
				yivi_chained_session: chainedSession,
				yivi_chained_session_drip: chainedSession,
			};

			// 3. Start authentication
			const { task, state } = await authServer.authStartEP(authStartReq);
			// A late response from a superseded run would overwrite the state its replacement is
			// already waiting to complete an authentication against.
			if (superseded()) throw new EnterCancelled();
			authServer.setState(state);

			// 4. Handle Yivi task
			if (loginMethod.source !== Source.Yivi || !task.Yivi) {
				throw new Error(`Task mismatch for source ${loginMethod.source}: ${JSON.stringify(task)}`);
			}
			const { disclosure_request, yivi_requestor_url } = task.Yivi;
			const yiviUrl = filters.removeTrailingSlash(yivi_requestor_url);

			// Disclose attributes in Yivi
			let proof: { Yivi: { disclosure: string } };

			this.awaitingDisclosure = true;
			try {
				if (chainedSession) {
					// When chaining, the disclosure is collected from the authentication server below rather
					// than from this promise, and `YiviWaitForResultEP` is what reports a failure. The Yivi
					// handler has already logged it, so the rejection is only kept from going unhandled here.
					this.runYiviSession(yiviUrl, disclosure_request, mountPoint, runId).catch(() => {});
					const jwt = await authServer.YiviWaitForResultEP(authServer.getState());
					if (jwt === 'PleaseRestartAuth') {
						throw new Error('Something went wrong; please start again at AuthStartEP.');
					} else if (jwt === 'SessionGone') {
						throw new Error('The session has expired or was already completed.');
					} else if (typeof jwt !== 'object' || !(ResultResponse.Success in jwt)) {
						throw new Error('Unexpected response from YiviWaitForResultEP');
					}
					proof = { Yivi: jwt.Success };
				} else {
					let disclosure: string;
					try {
						disclosure = await this.runYiviSession(yiviUrl, disclosure_request, mountPoint, runId);
					} catch {
						// A superseded run aborts its own Yivi session, which is not a failure to report.
						if (superseded()) throw new EnterCancelled();
						// Already logged by the Yivi handler. Reported instead of thrown so the caller can
						// show it in place of the QR code, rather than sending the user to the error page.
						return { key: 'errors.yivi_session_failed' };
					}
					proof = { Yivi: { disclosure } };
				}
			} finally {
				// A superseded run must not clear a flag its replacement has already raised.
				if (!superseded()) this.awaitingDisclosure = false;
			}

			// The disclosure the user just made belongs to the authentication state a newer run has
			// since replaced, so completing against it would fail - or worse, succeed for the wrong run.
			if (superseded()) throw new EnterCancelled();

			// 5. Complete authentication
			const authSuccess = await authServer.completeAuthEP(proof, authServer.getState());

			// 6. Validate attributes
			this.validateAttributes(authSuccess.attrs, isRegistering || !cardFeature, loginMethod);

			// 7. Decode attributes
			const { identifying, additional, attributeValues } = this.decodeSignedAttributes(authSuccess.attrs, identifyingAttrs);

			// 8. Enter PubHubs
			// ? If there are several identifying attributes pick the first one
			const identifyingHandle = Object.keys(identifying)[0];
			const identifyingAttr = authSuccess.attrs[identifyingHandle];
			const { entered, errorMessage } = await this.phcServer.enter(additional, enterMode, identifyingAttr, registerOnlyWithUniqueAttrs);
			if (!entered) return errorMessage;

			// 9. Issue a Pubhubs card if registering a new account
			// When chaining, the Yivi server has been waiting since step 4 (disclosure) and will
			// timeout after ~5 seconds; otherwise this starts a session of its own.
			// If card issuance fails due to network error (e.g., battery saver),
			// we return a warning so the user can retry card issuance separately.
			if (isRegistering && cardFeature) {
				const comment = 'via\n' + attributeValues.join('\n');
				this.issuingCard = true;
				try {
					const { cardAttr, errorMessage: cardError } = await this.issueCard(chainedSession, comment, mountPoint, identifyingAttr, runId);
					if (cardAttr) identifying['ph_card'] = cardAttr;
					else warningMessage = cardError;
				} catch (error) {
					// Card issuance failed (likely network error from battery saver)
					// Return warning so user can retry card issuance from the UI
					if (error instanceof TypeError && error.message.includes('NetworkError')) {
						warningMessage = { key: 'errors.YiviServerGone', values: [comment] };
					} else {
						throw error;
					}
				} finally {
					// Steps 8 onwards no longer touch the shared authentication state, so a superseded run
					// finishes them rather than leave the account half registered - but it must not clear a
					// flag its replacement has already raised.
					if (!superseded()) this.issuingCard = false;
				}
			}

			// 10. Load updated state (moved after card issuance to reduce Yivi wait time)
			await this.phcServer.stateEP();
			// Load Secret objects
			// An account that is entered but whose user secret cannot be resolved is not usable, so
			// every failure from here on abandons the entry rather than leaving the user half logged in.
			// `abandonEntry` clears the auth token and the cached user secret process-wide, though, so a
			// run that was *replaced* would tear down the session its replacement has meanwhile
			// established. It leaves both the cleanup and the reporting to the run that now owns that
			// state. A run that was cancelled with nothing taking over still owns it, and has to abandon
			// the entry itself - dropping it there is what leaves an account nobody can log in to again.
			const reqUserSecretResp = await this.requestUserSecretObject(identifying);
			if (reqUserSecretResp.error) {
				if (replaced()) throw new EnterCancelled();
				this.abandonEntry();
				return { key: reqUserSecretResp.message, values: reqUserSecretResp.values };
			}
			const { userSecret, userSecretBackup } = reqUserSecretResp;
			const userSecretObject = userSecret?.object ?? null;

			// 11. Get attribute Key Response
			const attrKeyResp = await this.requestAttrKeys(userSecretObject, identifying);
			if (attrKeyResp.error) {
				if (replaced()) throw new EnterCancelled();
				this.abandonEntry();
				return { key: attrKeyResp.response };
			}

			// 12. Store user secret objects
			const objectDetails = userSecret !== null ? { usersecret: userSecret.details, backup: userSecretBackup?.details ?? null } : null;
			try {
				await this.phcServer.storeUserSecretObject(attrKeyResp.response, identifying, userSecretObject, objectDetails);
			} catch (error) {
				logger.error('An error occured while trying to store the user secret.', error);
				if (replaced()) throw new EnterCancelled();
				this.abandonEntry();
				return { key: 'errors.general_error' };
			}
			return warningMessage;
		},

		/**
		 * Issue a PubHubs card for an account that is already entered, and store the user secret for
		 * it - the retry `enterPubHubs` reports through `errors.card_not_added`.
		 *
		 * The card is an identifying attribute, and it is the only one a later login discloses. A card
		 * that reaches the account without an entry in the user secret therefore locks the account for
		 * good, so the issuance only counts as done once both have happened.
		 *
		 * @param comment The attributes the card is issued for, shown in the user's Yivi app.
		 */
		async issueCardAfterEntry(comment: string, mountPoint: YiviMountPoint): Promise<ReturnCard> {
			const runId = currentEnterRun;
			// The page keys its widget restarts on this, so it has to go down once the Yivi session is
			// over: the write below renders nothing, and restarting there issues a second card and races
			// a second write against this one.
			this.issuingCard = true;
			const issued = await this.issueCard(false, comment, mountPoint).finally(() => {
				// A superseded issuance must not clear a flag the one that replaced it has already raised.
				if (runId === currentEnterRun) this.issuingCard = false;
			});
			if (!issued.cardAttr) return issued;

			try {
				await this.addCardToUserSecret(issued.cardAttr);
			} catch (error) {
				logger.error('The PubHubs card was issued but could not be stored in the user secret.', error);
				return { cardAttr: null, errorMessage: { key: 'errors.card_not_linked' } };
			}
			return issued;
		},

		/**
		 * Store the user secret for a PubHubs card that was added to the account after the login that
		 * stored the secret, so the next login can recover it from the card alone.
		 *
		 * @throws If the attribute key could not be fetched, or the objects could not be stored.
		 */
		async addCardToUserSecret(cardAttr: { signedAttr: string; id: string; value: string }): Promise<void> {
			const identifying: SignedIdentifyingAttrs = { ph_card: cardAttr };

			const reqUserSecretResp = await this.requestUserSecretObject(identifying);
			if (reqUserSecretResp.error) {
				throw new Error(`Could not resolve the user secret objects to add the card to: ${reqUserSecretResp.message}`);
			}
			const { userSecret, userSecretBackup } = reqUserSecretResp;

			const attrKeyResp = await this.requestAttrKeys(userSecret?.object ?? null, identifying);
			if (attrKeyResp.error) {
				throw new Error(`Could not get an attribute key for the PubHubs card: ${attrKeyResp.response}`);
			}

			const objectDetails = userSecret !== null ? { usersecret: userSecret.details, backup: userSecretBackup?.details ?? null } : null;
			await this.phcServer.addIdentifyingAttrToUserSecret(attrKeyResp.response, identifying, userSecret?.object ?? null, objectDetails);
		},

		/**
		 * Give up an entry that got as far as an auth token but cannot be completed, without navigating:
		 * the page that started it shows what happened and decides where the user goes next.
		 */
		abandonEntry(): void {
			useGlobal().loggedIn = false;
			this.logout();
		},

		async enterHub(id: string, enterStartResp: EnterStartResp): Promise<string | undefined> {
			const maxAttempts = 4;
			for (let attempt = 0; attempt < maxAttempts; attempt++) {
				if (attempt > 0) {
					delay(attempt - 1);
				}

				const sealedPPP = await this.phcServer.pppEP();
				assert.isDefined(sealedPPP, 'Something went wrong, sealedPPP should be defined.');
				const transcryptor = await this.getTranscryptor();
				const sealedEhpp = await transcryptor.ehppEP({ nonce: enterStartResp.nonce, id, ppp: sealedPPP, hubMacKey: enterStartResp.hub_mac_key });

				if (sealedEhpp === 'RetryWithNewPpp' && attempt < maxAttempts) {
					continue;
				} else if (sealedEhpp === 'RetryWithNewPpp') {
					throw new Error('Theres something wrong with the sso::EncryptedHubPseudonymPackage');
				}
				assert.isDefined(sealedEhpp, 'Something went wrong, sealedEhpp should be defined or you should have gone back to requesting a new Ppp.');
				const signedHhpp = await this.phcServer.hhppEP(sealedEhpp, enterStartResp.hhpp_signature_scheme);

				if (signedHhpp === 'RetryWithNewPpp' && attempt < maxAttempts) {
					continue;
				} else if (signedHhpp === 'RetryWithNewPpp') {
					throw new Error('Theres something wrong with the sso::EncryptedHubPseudonymPackage');
				}
				assert.isDefined(
					signedHhpp,
					'Something went wrong, signedHhpp should be defined or you should have logged out or gone back to requesting a new Ppp.',
				);
				return signedHhpp;
			}
		},

		/**
		 * @param ownedByRun The enter run this issuance belongs to, see `runYiviSession`. A page
		 * retrying the issuance on its own leaves it out.
		 */
		async issueCard(
			chainedSession: boolean,
			comment: string,
			mountPoint: YiviMountPoint,
			identifyingAttr?: string,
			ownedByRun = currentEnterRun,
		): Promise<ReturnCard> {
			const authServer = await this.getAuthServer();

			// 1. Fetch pseudo card package from the Pubhubs Central Server
			const pseudoRespSuccess = await this.phcServer.cardPseudoPackage();
			const cardReq: CardReq = {
				card_pseud_package: pseudoRespSuccess,
				comment: comment,
			};

			// 2. Get signed card attribute from Auth server
			const cardRespSuccess = await authServer.CardEP(cardReq);
			const { attr: signedCardAttr, issuance_request, yivi_requestor_url } = cardRespSuccess;

			// 3. Add card attribute to PubHubs Central Server
			const { entered, errorMessage } = await this.phcServer.enter([signedCardAttr], PHCEnterMode.Login, identifyingAttr);
			if (!entered) {
				return { cardAttr: null, errorMessage };
			}
			// 4. Add card to Yivi
			if (chainedSession) {
				const response = await authServer.YiviReleaseNextSessionEP({
					state: authServer.getState(),
					next_session: issuance_request,
					stale_after: 4500,
				});
				if (response === 'YiviServerGone') {
					return { cardAttr: null, errorMessage: { key: 'errors.YiviServerGone', values: [comment] } };
				}
			} else {
				const yiviUrl = filters.removeTrailingSlash(yivi_requestor_url);
				try {
					await this.runYiviSession(yiviUrl, issuance_request, mountPoint, ownedByRun);
				} catch {
					// The card attribute is on the account (step 3) but never reached the user's Yivi
					// app, and logging in later discloses exactly that card - so this has to be
					// reported, not swallowed.
					return { cardAttr: null, errorMessage: { key: 'errors.card_not_added', values: [comment] } };
				}
			}

			// 5. Decode and return card
			const decoded = decodeJWT(signedCardAttr) as Attr;

			return {
				cardAttr: {
					signedAttr: signedCardAttr,
					id: decoded.attr_type,
					value: decoded.value,
				},
				errorMessage: null,
			};
		},

		async storePHCWelcomeInfo(): Promise<void> {
			const welcomeResp = await this.phcServer.welcome();
			this.constellation = welcomeResp.constellation;
			this.hubs = welcomeResp.hubs;
		},

		async initializeServers(): Promise<void> {
			if (!this.constellation || !this.hubs) {
				await this.storePHCWelcomeInfo();
			}
			assert.isNotNull(this.constellation);
			this._authServer = new AuthenticationServer(filters.removeTrailingSlash(this.constellation.auths_url));
			this._transcryptor = new Transcryptor(filters.removeTrailingSlash(this.constellation.transcryptor_url));
		},

		async getAuthServer() {
			if (!this._authServer) {
				await this.initializeServers();
			}
			if (!this._authServer) {
				throw new Error('AuthServer failed to initialize');
			}
			return this._authServer;
		},

		async getTranscryptor(): Promise<{
			ehppEP: (args: { nonce: string; id: string; ppp: string; hubMacKey?: string }) => Promise<string>;
		}> {
			if (!this._transcryptor) {
				await this.initializeServers();
			}
			if (!this._transcryptor) {
				throw new Error('Transcryptor failed to initialize');
			}
			return this._transcryptor;
		},

		logout(): void {
			this.phcServer.reset();
			useLocalStores().clear();
			localStorage.removeItem('PHauthToken');
			localStorage.removeItem('UserSecret');
			localStorage.removeItem('UserSecretVersion');
		},

		async getHubs(): Promise<HubInformation[]> {
			if (!this.hubs) {
				await this.storePHCWelcomeInfo();
			}
			assert.isNotNull(this.hubs);
			return Object.values(this.hubs);
		},

		async requestUserObject(handle: string): Promise<string | null> {
			const object = await this.phcServer.getDecryptedUserObject(handle);
			return object;
		},

		async storeUserObject<T>(handle: string, data: T): Promise<void> {
			const object = await this.phcServer.getUserObject(handle);
			let overwriteHash: string | undefined = undefined;
			if (object) {
				// If the object already exists, the hash is needed to overwrite the object
				overwriteHash = object.details.hash;
			}
			await this.phcServer.encryptAndStoreObject<T>(handle, data, overwriteHash);
		},

		async getHubInfo(hubServerUrl: string): Promise<InfoResp> {
			const infoResp = await hub_api.api<HubInfoResp | InfoResp>(`${hubServerUrl}${hub_api.apiURLS.info}`);

			// hubs <= v3.0.0 do not wrap the info response in an "Ok": { ... }
			if (isResult(infoResp)) {
				return await handleErrors<InfoResp>(() => Promise.resolve(infoResp));
			}

			return infoResp;
		},

		decodeSignedAttributes(attributes: Record<string, string>, identifyingAttrs: Set<string>): DecodedAttributes {
			const identifying: Record<string, { signedAttr: string; id: string; value: string }> = {};
			const additional: string[] = [];
			const attributeValues: string[] = [];

			for (const [handle, signedAttr] of Object.entries(attributes)) {
				const decoded = decodeJWT(signedAttr) as Attr;

				if (identifyingAttrs.has(handle)) {
					identifying[handle] = {
						signedAttr,
						id: decoded.attr_type,
						value: decoded.value,
					};
				} else {
					additional.push(signedAttr);
				}
				attributeValues.push(decoded.value);
			}

			if (Object.keys(identifying).length === 0) {
				throw new Error('Identifying attribute missing in authentication response.');
			}

			return { identifying, additional, attributeValues };
		},
		validateAttributes(attrs: Record<string, unknown>, isRegisteringOrNoCard: boolean, loginMethod: LoginMethod): void {
			const keys = Object.keys(attrs);
			let allowedAttributeSets: string[];

			if (isRegisteringOrNoCard) {
				allowedAttributeSets = loginMethod.register_attr;
			} else {
				// At least one of the choices need to match for login
				allowedAttributeSets = loginMethod.login_attr;
			}

			if (!responseEqualToRequested(keys, allowedAttributeSets)) {
				throw new Error('Disclosed attributes do not match the requested ones.');
			}
		},

		buildAttributeKeyRequest(signedIdentifyingAttrs: SignedIdentifyingAttrs, userSecretObject: UserSecretObject | null): AuthAttrKeyReq {
			const attrKeyReq: AuthAttrKeyReq = {};

			for (const [handle, attr] of Object.entries(signedIdentifyingAttrs)) {
				let timestamp = null;

				if (isUserSecretObjectNew(userSecretObject)) {
					timestamp = userSecretObject?.data?.[attr.id]?.[attr.value]?.ts ?? null;
				} else if (userSecretObject) {
					timestamp = userSecretObject?.[attr.id]?.[attr.value]?.ts ?? null;
				}

				attrKeyReq[handle] = {
					attr: attr.signedAttr,
					timestamp,
				};
			}

			return attrKeyReq;
		},
	},
});

export { EnterCancelled, useMSS };
